// SUPER ADMIN DASHBOARD > CompaniesListPage > CompaniesListLayer

import { Icon } from "@iconify/react/dist/iconify.js";
import { saveAs } from "file-saver";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";

const EnquiryFormPagelayer = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [forms, setForms] = useState([]);
  const [remarks, setRemarks] = useState({});
  const [status, setStatus] = useState({});
  const [selectedFormId, setSelectedFormId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const recordsPerPage = 15;
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    companyName: "",
    remark: "",
    status: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ✅ Move fetchData outside useEffect
  const fetchData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/enquiry`
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);

      const data = await response.json();
      // Sort data by createdAt descending (newest first)
      const sortedData = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setForms(sortedData);
    } catch (error) {
      console.error("Error fetching forms:", error.message);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/enquiry/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      if (!response.ok) throw new Error("Failed to submit enquiry");
      await fetchData(); // refresh table
      setShowModal(false);
      setFormData({
        name: "",
        email: "",
        phone: "",
        companyName: "",
        remark: "",
        status: "",
      });
    } catch (error) {
      console.error("Error submitting enquiry:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPages = Math.ceil(forms.length / recordsPerPage);
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentUsers = forms.slice(indexOfFirstRecord, indexOfLastRecord);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Debounced function to update remark
  const debouncedUpdateRemark = useCallback(
    debounce(async (userId, data) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/enquiry/updateRemark/${userId}`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(data),
          }
        );

        const result = await response.json();
        if (!response.ok) {
          console.error(result.message || "Failed to update remark");
        }
        fetchData();
      } catch (error) {
        console.error("Error updating remark:", error);
      }
    }, 800), // Wait 800ms after typing stops
    []
  );

  const handleExportToExcel = () => {
    // Sample data structure - use your actual array of users
    const exportData = forms.map((user) => {
      return {
        Name: user.name,
        Email: user.email,
        Mobile: `+91 ${user.phone}`,
        "Company Name": user.companyName,
        "Created At": new Date(user.createdAt).toLocaleDateString(),
        Remark: user.remark,
        Status: user.status,
      };
    });

    // Create worksheet & workbook
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

    // Convert workbook to Excel buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Save using file-saver
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "Enquiry_form.xlsx");
  };

  const handleDelete = (id) => {
    setDeleteModal(true);
    setSelectedFormId(id);
  };

  const handleConfirm = async () => {
    if (!selectedFormId) return;

    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/enquiry/${selectedFormId}`
      );

      if (response.status === 200) {
        alert(response.data.message);
        setDeleteModal(false); // ✅ Close the modal on success
        fetchData();
      }
    } catch (error) {
      console.error("Error deleting enquiry:", error.message);
      alert("Failed to delete enquiry. Please try again."); // ✅ Show an alert on error
    } finally {
      setDeleteModal(false); // ✅ Ensure modal closes even on error
    }
  };
  return (
    <div className="card radius-16 mt-24">
      <div className="card-body">
        {/* Button to open popup form*/}
        <div className="d-flex align-items-center flex-wrap gap-4 justify-content-end mb-20">
          <button
            className="btn btn-outline-success d-inline-flex align-items-center gap-1 text-sm btn-sm px-8 py-6"
            onClick={handleExportToExcel}
          >
            <Icon icon="ph:arrow-up" className="icon text-md" />
            Export
          </button>
          <button
            className="btn btn-outline-primary d-inline-flex align-items-center gap-2 text-sm btn-sm px-8 py-6"
            onClick={() => setShowModal(true)}
          >
            <Icon icon="ph:plus-circle" className="icon text-xl" />
            Add Enquiry
          </button>
        </div>

        {/* Popup Form */}
        {forms.length === 0 ? (
          <div className="text-center py-5">
            <h4>No records found</h4>
            <p>There are no forms to display at the moment.</p>
          </div>
        ) : (
          <div className="table-responsive scroll-md">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th className="text-center">Name</th>
                  <th className="text-center">Email</th>
                  <th className="text-center">Mobile Number</th>
                  <th className="text-center">Company Name</th>
                  <th className="text-center">Created On</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Remark</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.map((user, index) => (
                  <tr key={index}>
                    <td className="text-center">{user.name}</td>
                    <td className="text-center">{user.email}</td>
                    <td className="text-center">+91 {user.phone}</td>
                    <td className="text-center">{user.companyName}</td>
                    <td className="text-center">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </td>
                    <td className="text-center">
                      <select
                        value={
                          status[user._id] ?? user.status ?? "Select Status"
                        }
                        onChange={(e) => {
                          const value = e.target.value;
                          setStatus((prev) => ({ ...prev, [user._id]: value }));
                          debouncedUpdateRemark(user._id, { status: value });
                        }}
                        style={{
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          height: "36px",
                          padding: "4px 8px",
                          fontSize: "14px",
                          minWidth: "120px",
                          outline: "none",
                          cursor: "pointer",
                        }}
                      >
                        <option selected disabled>
                          Select Status
                        </option>
                        <option value="RNR">RNR</option>
                        <option value="salesQualified">Sales Qualified</option>
                        <option value="quotation">Quotation</option>
                        <option value="techQualified">Tech Qualified</option>
                        <option value="nego">Nego</option>
                        <option value="won">Won</option>
                        <option value="lost">Lost</option>
                        <option value="indiamart">Indiamart</option>
                        <option value="otherSource">Other Source</option>
                      </select>
                    </td>
                    <td className="text-center">
                      <input
                        type="text"
                        value={remarks[user._id] ?? user.remark ?? ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRemarks((prev) => ({
                            ...prev,
                            [user._id]: value,
                          }));
                          debouncedUpdateRemark(user._id, { remark: value });
                        }}
                        className="form-control"
                      />
                    </td>
                    <td>
                      <span
                        className="w-32-px h-32-px me-8 bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center cursor-pointer"
                        onClick={() => handleDelete(user._id)}
                      >
                        <Icon icon="mingcute:delete-2-line" />
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {forms.length > 0 && (
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
            <span>
              Showing {indexOfFirstRecord + 1} to{" "}
              {Math.min(indexOfLastRecord, forms.length)} of {forms.length}{" "}
              entries
            </span>
            <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <Icon icon="ep:d-arrow-left" />
                </button>
              </li>
              {[...Array(totalPages).keys()].map((num) => (
                <li className="page-item" key={num + 1}>
                  <button
                    className={`page-link ${
                      currentPage === num + 1
                        ? "bg-primary-600 text-white"
                        : "bg-neutral-200 text-secondary-light"
                    } fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md`}
                    onClick={() => handlePageChange(num + 1)}
                  >
                    {num + 1}
                  </button>
                </li>
              ))}
              <li className="page-item">
                <button
                  className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <Icon icon="ep:d-arrow-right" />
                </button>
              </li>
            </ul>
          </div>
        )}

        {/* Form Modal */}
        <Dialog
          open={showModal}
          onClose={() => setShowModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h5" align="center" gutterTop fontWeight="bold">
              Create Enquiry
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setShowModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <TextField
                margin="normal"
                label="Name"
                name="name"
                fullWidth
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <TextField
                margin="normal"
                label="Email"
                name="email"
                type="email"
                fullWidth
                value={formData.email}
                onChange={handleInputChange}
                required
              />
              <TextField
                margin="normal"
                label="Phone"
                name="phone"
                fullWidth
                value={formData.phone}
                onChange={handleInputChange}
                inputProps={{
                  maxLength: 10,
                  inputMode: "numeric",
                  // pattern: "[6-9][0-9]{9}",
                }}
                onInput={(e) => {
                  e.target.value = e.target.value
                    .replace(/[^0-9]/g, "")
                    .slice(0, 10);
                }}
                required
              />
              <TextField
                margin="normal"
                label="Company Name"
                name="companyName"
                fullWidth
                value={formData.companyName}
                onChange={handleInputChange}
                required
              />
            </DialogContent>

            <DialogActions
              style={{
                justifyContent: "center",
                margin: "10px",
                gap: "10px",
              }}
            >
              <Button
                onClick={() => setShowModal(false)}
                color="error"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Confirmation Modal */}
        {deleteModal && (
          <div
            className="modal-backdrop"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1050,
            }}
          >
            <div
              className="modal-container"
              style={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                maxWidth: "400px",
                width: "90%",
                padding: "20px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                textAlign: "center",
              }}
            >
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5 className="modal-title">Confirm Action</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this admin?</p>
              </div>
              <div className="modal-footer d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleConfirm}
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiryFormPagelayer;
